import React, { useEffect, useContext, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import axios from 'axios';
import { toast } from 'sonner';
import { AuthContext } from '../App';
import { AuthDialog } from '../components/AuthDialog';
import { EnergyField, Logo } from '../components/EnergyField';
import { AudioToggle } from '../components/AmbientAudio';
import {
  ArrowRight, ArrowUpRight, Leaf, CloudLightning, Radio, Droplets,
  PlugZap, FlaskConical, FileText, Radar, Menu, X, Lock, Check, BookOpen,
} from 'lucide-react';
import { statusTag, readTime } from '../lib/meta';
import { ArchiveDepth } from '../components/ArchiveDepth';
import { WeeklyOpenRecord } from '../components/WeeklyOpenRecord';
import { HERO_BG as SHARED_HERO_BG, DOMAIN_IMAGES } from '../lib/domainImages';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const PENDING_DOMAIN = 'fff_pending_domain';

const HERO_BG = SHARED_HERO_BG;
const IMG = {
  electroculture: DOMAIN_IMAGES.electroculture,
  atmospheric: DOMAIN_IMAGES.atmospheric,
  resonance: DOMAIN_IMAGES.resonance,
  water: DOMAIN_IMAGES.water,
  offgrid: DOMAIN_IMAGES.offgrid,
  field: DOMAIN_IMAGES.field,
};

const DOMAINS = [
  { num: 'D-01', name: 'Electroculture', match: 'electro', ev: 'Experimental · Mixed evidence', desc: 'Atmospheric electricity and electromagnetic stimulation of plant systems.', Icon: Leaf, img: IMG.electroculture },
  { num: 'D-02', name: 'Atmospheric Energy', match: 'atmospher', ev: 'Observational · Experimental · Theoretical', desc: 'Ionospheric potential, field lines, and ambient electrical phenomena.', Icon: CloudLightning, img: IMG.atmospheric },
  { num: 'D-03', name: 'Resonance & Frequency', match: 'resonan', ev: 'Established physics · Exploratory applications', desc: 'Wave interference, cymatics, and frequency response experiments.', Icon: Radio, img: IMG.resonance },
  { num: 'D-04', name: 'Water Systems', match: 'water', ev: 'Engineering · Established + Experimental', desc: 'Filtration, structuring, hydrodynamics, and off-grid water treatment.', Icon: Droplets, img: IMG.water },
  { num: 'D-05', name: 'Off-Grid Systems', match: 'off-grid', ev: 'Engineering · Field practice', desc: 'Autonomous power, storage, and decentralized infrastructure.', Icon: PlugZap, img: IMG.offgrid },
  { num: 'D-06', name: 'Field Experiments', match: 'experiment', ev: 'Experimental · Field observation', desc: 'Documented prototype rigs, measurements, and replication notes.', Icon: FlaskConical, img: IMG.field },
];

const SUBTITLES = [
  'Researching the invisible architecture of nature.',
  'Experimental systems for a decentralized future.',
  'Resonance. Atmosphere. Water. Energy.',
  'Field research beyond conventional infrastructure.',
];

const NAV = [
  { label: 'Home', t: 'home' },
  { label: 'Research', t: 'domains' },
  { label: 'Blueprints', t: 'vault' },
  { label: 'Field Notes', t: 'notes' },
  { label: 'Experiments', t: 'domains' },
  { label: 'Updates', t: 'notes' },
];

const rise = { initial: { opacity: 0, y: 24 }, whileInView: { opacity: 1, y: 0 }, viewport: { once: true, margin: '-80px' }, transition: { duration: 0.8, ease: [0.16, 1, 0.3, 1] } };

const LandingPage = () => {
  const navigate = useNavigate();
  const { user, setUser } = useContext(AuthContext);
  const [loading, setLoading] = useState(false);
  const [authOpen, setAuthOpen] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [subIdx, setSubIdx] = useState(0);
  const [preview, setPreview] = useState([]);
  const [stats, setStats] = useState(null);
  const [weekly, setWeekly] = useState(null);

  const processSession = async (sessionId) => {
    setLoading(true);
    try {
      const response = await axios.post(`${API}/auth/session`, { session_id: sessionId });
      setUser(response.data.user);
      toast.success('Access granted');
      window.history.replaceState({}, document.title, window.location.pathname);
      navigate('/dashboard');
    } catch (error) {
      toast.error('Authentication failed');
      window.history.replaceState({}, document.title, window.location.pathname);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const hash = window.location.hash;
    if (hash && hash.includes('session_id=')) {
      const sessionId = hash.split('session_id=')[1].split('&')[0];
      void Promise.resolve().then(() => { processSession(sessionId); });
    } else if (user) {
      navigate('/dashboard');
    }
  }, [user]);

  useEffect(() => {
    const id = setInterval(() => setSubIdx((i) => (i + 1) % SUBTITLES.length), 4200);
    return () => clearInterval(id);
  }, []);

  useEffect(() => {
    void Promise.resolve().then(async () => {
      try {
        const [p, s, w] = await Promise.all([
          axios.get(`${API}/public/articles`),
          axios.get(`${API}/public/stats`),
          axios.get(`${API}/public/weekly`),
        ]);
        setPreview(p.data.slice(0, 6));
        setStats(s.data);
        setWeekly(w.data);
      } catch { /* ignore */ }
    });
  }, []);

  const openAuth = () => setAuthOpen(true);
  const enterDomain = (match) => { localStorage.setItem(PENDING_DOMAIN, match); openAuth(); };
  const scrollTo = (id) => { setMenuOpen(false); document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' }); };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#08080A]">
        <div className="text-center space-y-6">
          <div className="spinner mx-auto"></div>
          <p className="mono-label">Authenticating</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#08080A] text-[#E2E4E9] overflow-x-hidden">
      <AuthDialog open={authOpen} onOpenChange={setAuthOpen} />

      {/* NAV */}
      <nav className="fixed top-0 left-0 right-0 z-50 glass-hud border-b border-[#262A36]">
        <div className="container mx-auto px-6 lg:px-12 py-4 flex justify-between items-center gap-8">
          <button onClick={() => scrollTo('home')} data-testid="nav-logo" className="shrink-0"><Logo /></button>
          <div className="hidden xl:flex items-center gap-6">
            {NAV.map((item) => (
              <button key={item.label} onClick={() => scrollTo(item.t)} className="font-sans-tech text-[0.82rem] text-[#949BAA] hover:text-[#E2E4E9] transition-colors" data-testid={`nav-${item.label.toLowerCase().replace(/\s/g, '-')}`}>
                {item.label}
              </button>
            ))}
            <AudioToggle />
            <button onClick={openAuth} className="btn-ambient !py-2 !px-5 !text-[0.72rem] shrink-0" data-testid="nav-login-button">Account / Admin</button>
          </div>
          <div className="xl:hidden flex items-center gap-4">
            <AudioToggle />
            <button onClick={openAuth} className="btn-ambient !py-2 !px-4 !text-[0.7rem] shrink-0" data-testid="nav-login-button-mobile">Account</button>
            <button className="text-white" onClick={() => setMenuOpen(!menuOpen)} data-testid="nav-menu-toggle">
              {menuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>
        {menuOpen && (
          <div className="xl:hidden glass-hud border-t border-[#262A36] px-6 py-4 flex flex-col gap-3">
            {NAV.map((item) => (
              <button key={item.label} onClick={() => scrollTo(item.t)} className="text-left text-[#949BAA] py-1 font-sans-tech">{item.label}</button>
            ))}
            <button onClick={openAuth} className="btn-ambient mt-2" data-testid="nav-login-button-menu">Account / Admin</button>
          </div>
        )}
      </nav>

      {/* HERO */}
      <section id="home" className="relative min-h-screen flex items-center pt-24 pb-16 px-6 lg:px-12 scanlines">
        <div className="absolute inset-0 z-0">
          <img src={HERO_BG} alt="" className="w-full h-full object-cover opacity-30" />
          <div className="absolute inset-0 bg-gradient-to-b from-[#08080A]/75 via-[#08080A]/88 to-[#08080A]" />
        </div>
        <EnergyField lines={6} particleCount={22} />
        <div className="container mx-auto max-w-5xl relative z-10">
          <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 1 }} className="mono-label mb-7 flex items-center gap-3">
            <Radar className="w-3.5 h-3.5" /> Experimental Research Archive · Node 0xFF
          </motion.p>
          <motion.h1 initial={{ opacity: 0, y: 26 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 1, ease: [0.16, 1, 0.3, 1] }} className="font-serif-display font-light tracking-tight text-5xl sm:text-7xl lg:text-[5.8rem] leading-[1.02]">
            Harness the<br />
            <span className="gradient-text text-glow-teal italic">Hidden Fields</span> of Nature
          </motion.h1>
          <div className="h-7 mt-8 overflow-hidden">
            <motion.p key={subIdx} initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }} className="font-sans-tech text-base sm:text-lg text-[#949BAA]">
              {SUBTITLES[subIdx]}
            </motion.p>
          </div>
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 1, delay: 0.4 }} className="mt-12 flex flex-col sm:flex-row items-start sm:items-center gap-4">
            <button onClick={openAuth} className="btn-primary" data-testid="hero-enter-lab-button">Enter Research Lab <ArrowRight className="w-4 h-4" /></button>
            <button onClick={() => scrollTo('vault')} className="btn-ambient" data-testid="hero-vault-button">View Blueprint Vault</button>
            <span className="mono-meta sm:ml-2">Research Access · €9 / month</span>
          </motion.div>
        </div>
      </section>

      {/* DOMAINS */}
      <section id="domains" className="relative py-28 px-6 lg:px-12 border-t border-[#262A36]/60">
        <div className="container mx-auto max-w-6xl">
          <motion.div {...rise} className="mb-16">
            <p className="mono-label mb-4">Research Domains · 06 Active</p>
            <h2 className="font-serif-display text-4xl lg:text-6xl font-light">Domain Portals</h2>
            <p className="font-sans-tech text-[#949BAA] mt-5 max-w-2xl">An independent archive spanning established engineering, emerging research, field observation, and carefully labelled experimental inquiry. We explore broad territory — without flattening the differences in evidence. Each portal opens field logs, prototype guides, and blueprints, every record carrying a visible evidence class.</p>
          </motion.div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="research-domains">
            {DOMAINS.map(({ num, name, desc, ev, Icon, img, match }, i) => (
              <motion.div
                key={num}
                {...rise}
                transition={{ duration: 0.7, delay: (i % 3) * 0.08, ease: [0.16, 1, 0.3, 1] }}
                className="portal aspect-[4/5] group"
                onClick={() => enterDomain(match)}
                data-testid={`domain-${num}`}
              >
                <img src={img} alt={name} className="portal-img" />
                <div className="portal-overlay" />
                <div className="relative z-10 h-full flex flex-col justify-between p-7">
                  <div className="flex justify-between items-start">
                    <div className="w-11 h-11 rounded-lg border border-white/15 bg-black/30 backdrop-blur flex items-center justify-center">
                      <Icon className="w-5 h-5 text-[#26B0A8]" />
                    </div>
                    <span className="mono-meta">{num}</span>
                  </div>
                  <div>
                    <h3 className="font-serif-display text-2xl font-medium mb-2">{name}</h3>
                    <p className="font-sans-tech text-sm text-[#AEB6C4]/80 leading-relaxed">{desc}</p>
                    <p className="mono-meta text-[#5b626f] mt-3 pl-2 border-l-2 border-[#3E7C78]/50" data-testid={`domain-evidence-${num}`}>{ev}</p>
                    <div className="mt-4 flex items-center gap-2 text-[#26B0A8] text-xs font-mono-tech uppercase tracking-widest opacity-0 group-hover:opacity-100">
                      Open portal <ArrowUpRight className="w-3.5 h-3.5" />
                    </div>
                  </div>
                </div>
                <div className="portal-sweep" />
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ARCHIVE PREVIEW — real public teaser */}
      {preview.length > 0 && (
        <section id="archive" className="relative py-28 px-6 lg:px-12 border-t border-[#262A36]/60">
          <div className="container mx-auto max-w-6xl">
            <motion.div {...rise} className="mb-14">
              <p className="mono-label mb-4">Open Archive · Partial Visibility</p>
              <h2 className="font-serif-display text-4xl lg:text-6xl font-light">Inside the Archive</h2>
              <p className="font-sans-tech text-[#949BAA] mt-5 max-w-2xl">A partial view of the research network. Selected records are open; the rest unlock with Research Access.</p>
              <ArchiveDepth stats={stats} className="mt-5" />
            </motion.div>
            {weekly && (
              <motion.div {...rise} className="mb-12">
                <WeeklyOpenRecord record={weekly} onOpen={(rid) => navigate(`/article/${rid}`)} />
              </motion.div>
            )}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="archive-preview">
              {preview.map((a, i) => {
                const tag = statusTag(a.status);
                const locked = a.locked;
                return (
                  <motion.div
                    key={a.id} {...rise} transition={{ duration: 0.7, delay: (i % 3) * 0.07, ease: [0.16, 1, 0.3, 1] }}
                    onClick={() => navigate(`/article/${a.id}`)} className="r-card p-6 flex flex-col group cursor-pointer" data-testid={`preview-${a.id}`}
                  >
                    <div className="flex justify-between items-start mb-4">
                      <span className={`tag ${tag.cls}`}>{tag.label}</span>
                      {locked
                        ? <span className="tag tag-muted flex items-center gap-1"><Lock className="w-3 h-3" /> Restricted</span>
                        : <span className="tag tag-teal flex items-center gap-1"><BookOpen className="w-3 h-3" /> Public</span>}
                    </div>
                    <p className="mono-meta mb-2">{a.content_type || 'Field Note'}</p>
                    <h3 className="font-serif-display text-xl font-medium mb-2 group-hover:text-[#26B0A8] transition-colors">{a.title}</h3>
                    <p className="font-sans-tech text-sm text-[#949BAA] leading-relaxed flex-1">{a.description}</p>
                    <div className="flex items-center justify-between mt-5">
                      {locked
                        ? <span className="mono-meta flex items-center gap-1.5 text-[#6B7280]"><Lock className="w-3 h-3" /> Research Access</span>
                        : <span className="mono-meta">{readTime(a.content)} min read</span>}
                      <span className="flex items-center gap-1.5 text-[#26B0A8] text-xs font-mono-tech uppercase tracking-widest">{locked ? 'Preview' : 'Open'} <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" /></span>
                    </div>
                  </motion.div>
                );
              })}
            </div>
          </div>
        </section>
      )}

      {/* FEATURED RESEARCH LOG */}
      <section className="relative py-28 px-6 lg:px-12 border-t border-[#262A36]/60">
        <div className="container mx-auto max-w-6xl grid lg:grid-cols-2 gap-12 items-center">
          <motion.div {...rise} className="relative rounded-xl overflow-hidden border border-[#262A36] aspect-[4/3]">
            <img src={IMG.field} alt="Featured research log" className="w-full h-full object-cover opacity-70" style={{ filter: 'grayscale(0.3) brightness(0.7)' }} />
            <div className="absolute inset-0 bg-gradient-to-t from-[#08080A] via-transparent to-transparent" />
            <span className="absolute top-4 left-4 tag tag-teal">Research Summary</span>
          </motion.div>
          <motion.div {...rise} transition={{ duration: 0.8, delay: 0.1 }}>
            <p className="mono-label mb-4">Featured Field Log · FL-0142</p>
            <h2 className="font-serif-display text-3xl lg:text-5xl font-light mb-6 leading-tight">Copper Antenna Geometry & Soil Conductivity</h2>
            <p className="font-sans-tech text-[#949BAA] leading-relaxed mb-7">A season-long electroculture study documenting antenna geometry, measured conductivity shifts, and grounded observations on growth variance — with the full prototype guide and measurement log.</p>
            <div className="flex flex-wrap gap-2 mb-8">
              <span className="tag tag-steel">Field Test</span>
              <span className="tag tag-muted">Beginner</span>
              <span className="tag tag-teal">Electroculture</span>
            </div>
            <button onClick={openAuth} className="btn-ambient" data-testid="featured-open-button">Read Field Log <ArrowRight className="w-4 h-4" /></button>
          </motion.div>
        </div>
      </section>

      {/* BLUEPRINT VAULT */}
      <section id="vault" className="relative py-28 px-6 lg:px-12 border-t border-[#262A36]/60">
        <div className="container mx-auto max-w-6xl">
          <motion.div {...rise} className="flex flex-col md:flex-row md:items-end justify-between mb-16 gap-5">
            <div>
              <p className="mono-label mb-4">Classified Archive</p>
              <h2 className="font-serif-display text-4xl lg:text-6xl font-light">Blueprint Vault</h2>
            </div>
            <button onClick={openAuth} className="btn-ambient self-start md:self-auto" data-testid="vault-unlock-button">Unlock Vault <ArrowRight className="w-4 h-4" /></button>
          </motion.div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6" data-testid="vault-preview">
            {[
              { t: 'Atmospheric Energy Collector', tag: 'Experimental Blueprint', cls: 'tag-teal', meta: 'PDF · 3.2 MB · Advanced' },
              { t: 'Gravity-Fed Water Filter', tag: 'Prototype Guide', cls: 'tag-steel', meta: 'PDF · 1.8 MB · Beginner' },
              { t: 'Off-Grid Resonance Coil', tag: 'Experimental Blueprint', cls: 'tag-amber', meta: 'PDF · 4.6 MB · Advanced' },
            ].map((b, i) => (
              <motion.div key={i} {...rise} transition={{ duration: 0.7, delay: i * 0.08 }} className="r-card overflow-hidden cursor-pointer" onClick={openAuth} data-testid={`vault-item-${i}`}>
                <div className="relative h-40 overflow-hidden">
                  <img src={IMG.field} alt="" className="w-full h-full object-cover opacity-40" style={{ filter: 'grayscale(0.5) brightness(0.6)' }} />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#0c0f17] to-transparent" />
                  <FileText className="absolute bottom-4 left-4 w-6 h-6 text-[#26B0A8]" />
                </div>
                <div className="p-6">
                  <span className={`tag ${b.cls}`}>{b.tag}</span>
                  <h3 className="font-serif-display text-xl font-medium mt-3">{b.t}</h3>
                  <p className="mono-meta mt-3">{b.meta}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* FIELD NOTES */}
      <section id="notes" className="relative py-28 px-6 lg:px-12 border-t border-[#262A36]/60">
        <div className="container mx-auto max-w-5xl">
          <motion.div {...rise} className="mb-14">
            <p className="mono-label mb-4">Live Transmissions · Monthly</p>
            <h2 className="font-serif-display text-4xl lg:text-6xl font-light">Field Updates</h2>
          </motion.div>
          <div className="space-y-8">
            {[
              { ts: '2026.05 · 04:12 UTC', t: 'Static charge variance across humid vs. dry soil beds', d: 'New field note logged' },
              { ts: '2026.04 · 22:07 UTC', t: 'Comparing three low-cost water structuring methods', d: 'Research summary published' },
              { ts: '2026.03 · 09:48 UTC', t: 'Resonance coil tuning — what held up under repeated test', d: 'Experiment log archived' },
            ].map((n, i) => (
              <motion.div key={i} {...rise} transition={{ duration: 0.7, delay: i * 0.07 }} className="transmission cursor-pointer group" onClick={openAuth} data-testid={`field-note-${i}`}>
                <span className="transmission-dot" />
                <p className="mono-meta text-[#1E8F89] mb-1">{n.ts}</p>
                <h3 className="font-serif-display text-2xl font-light group-hover:text-[#26B0A8] transition-colors">{n.t}</h3>
                <p className="font-sans-tech text-sm text-[#949BAA] mt-1">{n.d}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* MEMBERSHIP */}
      <section id="subscribe" className="relative py-32 px-6 lg:px-12 border-t border-[#262A36]/60 overflow-hidden">
        <EnergyField lines={4} particleCount={14} />
        <div className="container mx-auto max-w-5xl relative z-10">
          <motion.div {...rise} className="text-center mb-14">
            <p className="mono-label mb-5">Membership · Research Network</p>
            <h2 className="font-serif-display text-4xl lg:text-7xl font-light mb-6">Access the <span className="gradient-text italic">Research Network</span></h2>
            <p className="font-sans-tech text-[#949BAA] max-w-xl mx-auto">Begin in the open archive. Step deeper with Research Access — deeper research, technical records, blueprints and connected evidence.</p>
          </motion.div>
          <div className="grid md:grid-cols-2 gap-6 max-w-3xl mx-auto">
            <motion.div {...rise} className="glass-panel rounded-2xl p-8" data-testid="tier-free">
              <p className="mono-label mb-3">Free Access</p>
              <div className="flex items-end gap-2 mb-6"><span className="font-serif-display text-5xl font-light text-white">€0</span></div>
              <ul className="space-y-3">
                {['Selected public field notes & open records', 'The Off-Grid Water Systems field guide', 'Visible evidence class on every record', 'Weekly open archive record', 'Teaser archive browsing & previews'].map((f) => (
                  <li key={f} className="flex items-start gap-2.5"><Check className="w-4 h-4 text-[#6B7280] shrink-0 mt-0.5" /><span className="font-sans-tech text-sm text-[#949BAA]">{f}</span></li>
                ))}
              </ul>
              <button onClick={openAuth} className="btn-quiet w-full mt-7" data-testid="free-access-button">Create Account</button>
            </motion.div>
            <motion.div {...rise} transition={{ duration: 0.8, delay: 0.08 }} className="glass-hud rounded-2xl p-8 reflect-top relative border border-[#1E8F89]/30" data-testid="tier-research">
              <span className="absolute top-5 right-5 tag tag-teal">Recommended</span>
              <p className="mono-label mb-3 text-[#26B0A8]">Research Access</p>
              <div className="flex items-end gap-2 mb-6"><span className="font-serif-display text-5xl font-light text-white">€9</span><span className="text-[#949BAA] mb-2 font-sans-tech">/ month</span></div>
              <ul className="space-y-3">
                {['Full archive & complete source trails', 'Blueprint Vault — PDF downloads & schematics', 'Comparative reviews & curated synthesis', 'Materials lists, measurement tables & build specs', 'Experiment logs, failure modes & practical limits', 'Full semantic traversal of the connected archive'].map((f) => (
                  <li key={f} className="flex items-start gap-2.5"><Check className="w-4 h-4 text-[#26B0A8] shrink-0 mt-0.5" /><span className="font-sans-tech text-sm text-[#C8CDD6]">{f}</span></li>
                ))}
              </ul>
              <button onClick={openAuth} className="btn-primary w-full mt-7" data-testid="subscribe-cta-button">Enter Research Lab <ArrowRight className="w-4 h-4" /></button>
              <p className="mono-meta text-center mt-4">Instant access · Cancel anytime</p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* FOOTER */}
      <footer className="py-12 px-6 lg:px-12 border-t border-[#262A36]" data-testid="site-footer">
        <div className="container mx-auto max-w-6xl flex flex-col md:flex-row justify-between items-center gap-6">
          <Logo />
          <div className="flex flex-col items-center md:items-end gap-2 text-center md:text-right">
            <div className="flex items-center gap-4 mb-1">
              <button onClick={() => navigate('/guides/off-grid-water-systems')} className="mono-meta text-[#949BAA] hover:text-[#26B0A8] transition-colors" data-testid="footer-guide-link">Water Systems Field Guide</button>
              <button onClick={() => navigate('/method')} className="mono-meta text-[#949BAA] hover:text-[#26B0A8] transition-colors" data-testid="footer-method-link">Research Method</button>
            </div>
            <div>
              <p className="mono-meta text-[#5b626f] mb-0.5">Research Contact</p>
              <a href="mailto:research@freefieldflow.com" className="mono-meta text-[#26B0A8] hover:text-[#34d3ca] transition-colors" data-testid="footer-contact-email">research@freefieldflow.com</a>
            </div>
            <p className="mono-meta">© 2026 FreeFieldFlow · A living archive of experimental field research</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
